<div class="warning-header">
  <div class="header-left">
    <div class="title-area">
      <mat-icon>warning</mat-icon>
      <h2 mat-dialog-title>{{ 'affectedPackaging.documents.title' | transloco }}</h2>
    </div>
  </div>
  <button mat-icon-button color="primary" type="button" (click)="doReject()">
    <mat-icon>close</mat-icon>
  </button>
</div>

<mat-dialog-content>
  <form *ngIf="showDocumentSection" [formGroup]="form" class="documents-section">
    <p>{{ 'affectedPackaging.documents.description' | transloco }}</p>

    <p *ngIf="!allowRecreate" class="icon-text-container mode-warning">
      <mat-icon class="margin-icon">delete_outline</mat-icon>
      <span>{{ 'affectedPackaging.documents.deleteOnlyNotice' | transloco }}</span>
    </p>

    <mat-radio-group *ngIf="allowRecreate" formControlName="mode" class="mode-group">
      <mat-radio-button [value]="modes.Delete">
        <span class="mode-label">{{ 'affectedPackaging.documents.deleteLabel' | transloco }}</span>
        <span class="hint mode-description">{{ 'affectedPackaging.documents.deleteDescription' | transloco }}</span>
      </mat-radio-button>
      <mat-radio-button [value]="modes.Recreate" [disabled]="!canRecreate">
        <span class="mode-label">{{ 'affectedPackaging.documents.recreateLabel' | transloco }}</span>
        <span class="hint mode-description">{{ 'affectedPackaging.documents.recreateDescription' | transloco }}</span>
      </mat-radio-button>
    </mat-radio-group>

    <table *ngIf="isRecreate && (tdOptions || docOptions)" class="options-table">
      <tr class="options-table-header">
        <th>{{ 'affectedPackaging.documents.optionsTableType' | transloco }}</th>
        <th>{{ 'affectedPackaging.documents.optionsTableLanguage' | transloco }}</th>
        <th>{{ 'affectedPackaging.documents.optionsTableDetails' | transloco }}</th>
        <th>{{ 'affectedPackaging.documents.optionsTableProfile' | transloco }}</th>
        <th></th>
      </tr>
      <tr *ngIf="tdOptions">
        <td class="options-table-type">{{ 'affectedPackaging.documents.technicalDocumentationLabel' | transloco }}</td>
        <td>{{ tdOptions.languageName }}</td>
        <td>&ndash;</td>
        <td>{{ tdOptions.tdProfileName || ('createDoc.createDocDialog.docProfileNone' | transloco) }}</td>
        <td>
          <button mat-stroked-button type="button" (click)="openTdOptionsDialog()">
            {{ 'affectedPackaging.documents.optionsChange' | transloco }}
          </button>
        </td>
      </tr>
      <tr *ngIf="docOptions">
        <td class="options-table-type">{{ 'affectedPackaging.documents.declarationOfConformityLabel' | transloco }}</td>
        <td>{{ docOptions.languageName }}</td>
        <td>{{ docOptions.manufacturerName }} &middot; {{ docOptions.signerName }}</td>
        <td>{{ docOptions.docProfileName || ('createDoc.createDocDialog.docProfileNone' | transloco) }}</td>
        <td>
          <button mat-stroked-button type="button" (click)="openDocOptionsDialog()">
            {{ 'affectedPackaging.documents.optionsChange' | transloco }}
          </button>
        </td>
      </tr>
    </table>

    <p *ngIf="organizationLoadFailed" class="icon-text-container mode-warning">
      <mat-icon class="margin-icon" color="warn">error</mat-icon>
      <span>{{ 'affectedPackaging.documents.organizationLoadFailed' | transloco }}</span>
    </p>

    <p *ngIf="unexpectedErrorOccurred" class="icon-text-container mode-warning">
      <mat-icon class="margin-icon" color="warn">error</mat-icon>
      <span>{{ 'affectedPackaging.documents.unexpectedError' | transloco }}</span>
    </p>

    <p *ngIf="allowRecreate && !allWritable" class="icon-text-container mode-warning">
      <mat-icon class="margin-icon" color="warn">lock</mat-icon>
      <span>{{ 'affectedPackaging.documents.recreateUnavailable' | transloco: { packaging: nonWritableNames } }}</span>
    </p>

    <p *ngIf="allowRecreate && hasForeignOrganization" class="icon-text-container mode-warning">
      <mat-icon class="margin-icon" color="warn">domain_disabled</mat-icon>
      <span>
        {{ 'affectedPackaging.documents.recreateForeignOrganization' | transloco: { organizations: foreignOrganizationNames } }}
        <ng-container *ngIf="hasDeclarationOfConformity">
          {{ 'affectedPackaging.documents.recreateForeignOrganizationDoc' | transloco }}
        </ng-container>
      </span>
    </p>
  </form>

  <p *ngIf="hasDocuments && !canRecreate" class="icon-text-container mode-warning documents-section">
    <mat-icon class="margin-icon">delete_outline</mat-icon>
    <span>{{ 'affectedPackaging.documents.deleteForcedNotice' | transloco }}</span>
  </p>

  <p *ngIf="affectedCount === 0" class="table-heading">{{ 'affectedPackaging.dialog.messageOnlyEdited' | transloco }}</p>
  <p *ngIf="affectedCount > 0" class="table-heading">
    {{ (affectedCount === 1 ? 'affectedPackaging.dialog.messageSingle' : 'affectedPackaging.dialog.messageMultiple') | transloco: { count: affectedCount } }}
  </p>

  <div class="table-container-wide">
    <table mat-table [dataSource]="dataSource" matSort>
      <ng-container matColumnDef="packagingType">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.packagingType' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">{{ getPackagingTypeKey(element.packagingType) | transloco }}</td>
      </ng-container>

      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.name' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.name }}
          <span *ngIf="element.isEditedPackaging" class="edited-marker">
            {{ 'affectedPackaging.table.editedPackaging' | transloco }}
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="articleNumber">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.articleNumber' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">{{ element.articleNumber }}</td>
      </ng-container>

      <ng-container matColumnDef="brandName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.brandName' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">{{ element.brandName }}</td>
      </ng-container>

      <ng-container matColumnDef="version">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.version' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.version }}<span *ngIf="element.versionName"> &ndash; {{ element.versionName }}</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="organizationName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'affectedPackaging.table.organizationName' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">{{ element.organizationName }}</td>
      </ng-container>

      <ng-container matColumnDef="recreateAllowed">
        <th mat-header-cell *matHeaderCellDef [matTooltip]="'affectedPackaging.table.recreateAllowedTooltip' | transloco">
          {{ 'affectedPackaging.table.recreateAllowed' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">
          <mat-icon *ngIf="isRecreateAllowedFor(element)">check</mat-icon>
          <span *ngIf="!isRecreateAllowedFor(element)">&ndash;</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="technicalDocumentation">
        <th mat-header-cell *matHeaderCellDef [matTooltip]="'ppwrDocuments.typeTd' | transloco">
          {{ 'affectedPackaging.table.technicalDocumentation' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">
          <mat-icon *ngIf="element.hasTechnicalDocumentation">check</mat-icon>
          <span *ngIf="!element.hasTechnicalDocumentation">&ndash;</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="declarationOfConformity">
        <th mat-header-cell *matHeaderCellDef [matTooltip]="'ppwrDocuments.typeDoc' | transloco">
          {{ 'affectedPackaging.table.declarationOfConformity' | transloco }}
        </th>
        <td mat-cell *matCellDef="let element">
          <mat-icon *ngIf="element.hasDeclarationOfConformity">check</mat-icon>
          <span *ngIf="!element.hasDeclarationOfConformity">&ndash;</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="inaccessibleLabel">
        <td mat-cell *matCellDef="let element" [attr.colspan]="inaccessibleLabelSpan" class="inaccessible-label">
          <span class="icon-text-container">
            <mat-icon class="margin-icon">visibility_off</mat-icon>
            {{ 'affectedPackaging.table.inaccessible' | transloco: { count: element.count } }}
          </span>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns; when: isAccessibleRow"></tr>
      <tr mat-row *matRowDef="let row; columns: inaccessibleColumns; when: isInaccessibleRow"></tr>
    </table>
  </div>
</mat-dialog-content>

<mat-dialog-actions>
  <button (click)="doReject()" mat-raised-button type="button">
    {{ 'common.buttons.cancel' | transloco }}
  </button>
  <button (click)="doConfirm()" mat-raised-button type="button" color="primary">
    {{ 'common.buttons.save' | transloco }}
  </button>
</mat-dialog-actions>
